"use client";

import { useEffect, useState, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import { BrandMark } from "@/components/brand-mark";
import { useAuth } from "@/contexts/auth-context";
import { VerifyEmailNotice } from "@/components/verify-email-notice";
import { OnboardingGateScreen } from "@/components/onboarding-gate";
import {
  FEATURE_ONBOARDING_GATE,
  useDisabledFeaturesState,
} from "@/lib/feature-flags";
import { setActiveWorkspacePin } from "@/lib/api";
import { getActiveWorkspaceTenantId } from "@/lib/api/workspaces";
import { STAFF_AS_PARAM } from "@/hooks/use-staff-task-deep-link";

/**
 * True when the URL carries a `?w=` workspace deep-link that still needs to
 * switch (or tidy) before we can decide the onboarding gate for the right
 * tenant. Mirrors AppShell's mount hop — AuthGuard must run it first because
 * the gate can return before AppShell mounts (codex P2 on #2045).
 *
 * Staff hops (`?as=`) are NOT pending here — `useStaffTaskDeepLink` in
 * AppShell owns those (must run without a full reload). AuthGuard skips the
 * gate while `?as=` is present so AppShell can mount.
 */
function pendingWorkspaceDeepLink(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  // Match staffDeepLinkPending — empty `?as=` is not a real hop.
  const as = params.get(STAFF_AS_PARAM);
  if (as && as.trim()) return false;
  return Boolean(params.get("w"));
}

function staffDeepLinkPending(): boolean {
  if (typeof window === "undefined") return false;
  // Only a non-empty `?as=<uid>` is a real staff hop. Bare `?as=` / `?as=&…`
  // must not skip the onboarding gate forever — useStaffTaskDeepLink ignores
  // empty values and never strips them (codex P2 on #2045).
  const as = new URLSearchParams(window.location.search).get(STAFF_AS_PARAM);
  return Boolean(as && as.trim());
}

export function AuthGuard({ children }: { children: ReactNode }) {
  const {
    user,
    loading,
    me,
    org,
    bootstrapError,
    retryBootstrap,
    needsEmailVerification,
    onboardingState,
    onboardingApiReady,
  } = useAuth();
  const router = useRouter();

  // Workspace deep-link hop must win over the onboarding gate: otherwise a
  // shared-project `?w=` link into an already-onboarded tenant is blocked by
  // the *current* tenant's unanswered gate and never reaches AppShell.
  const [workspaceHopPending, setWorkspaceHopPending] = useState(
    pendingWorkspaceDeepLink,
  );

  // Defer the settings/flags fetch until bootstrap has produced `me`, and key
  // the cache to the active tenant so a principal switch can't reuse another
  // workspace's disabled list (codex P2 on #2045). Hold boot until flags
  // resolve — unless the fetch fails, in which case proceed with the gate
  // treated as off rather than wedging on the spinner forever.
  const {
    disabled,
    loaded: featuresLoaded,
    failed: featuresFailed,
  } = useDisabledFeaturesState({
    // Wait out a pending `?w=` hop before fetching flags — otherwise we'd
    // load (and briefly cache) the *current* tenant's disabled list, then
    // reload into the deep-linked one.
    enabled: Boolean(me) && !workspaceHopPending,
    cacheKey: org?.id ?? me?.uid ?? null,
  });
  // Require both: flag granted (not in disabled_features) AND an API that
  // knows the gate (`onboarding_state` key on /api/me). Without the latter,
  // a web-ahead-of-API deploy would treat a missing flag entry as "granted"
  // and block every tenant (codex P1 on #2045).
  const gateEnabled =
    featuresLoaded &&
    onboardingApiReady &&
    !disabled.has(FEATURE_ONBOARDING_GATE);

  useEffect(() => {
    if (!loading && !user) {
      // Preserve the original destination (path + query) so the login page can
      // return the user there after sign-in — otherwise a signed-out recipient
      // of a deep link (e.g. a shared-project email's /project/<id>?w=<tenant>)
      // lands on the default route instead of the page they clicked.
      const here =
        typeof window !== "undefined"
          ? window.location.pathname + window.location.search
          : "/";
      const isDefault = here === "/" || here === "";
      router.replace(isDefault ? "/login" : `/login?next=${encodeURIComponent(here)}`);
    }
  }, [user, loading, router]);

  useEffect(() => {
    if (!me || !workspaceHopPending) return;
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.has(STAFF_AS_PARAM)) {
      setWorkspaceHopPending(false);
      return;
    }
    const w = params.get("w");
    if (!w) {
      setWorkspaceHopPending(false);
      return;
    }
    params.delete("w");
    const qs = params.toString();
    const clean = window.location.pathname + (qs ? `?${qs}` : "");
    if (w !== getActiveWorkspaceTenantId()) {
      setActiveWorkspacePin(w);
      window.location.assign(clean);
      return; // navigation in flight — keep showing boot
    }
    window.history.replaceState(null, "", clean);
    setWorkspaceHopPending(false);
  }, [me, workspaceHopPending]);

  if (loading) {
    return <AppBootScreen />;
  }

  // Keep showing the boot screen while router.replace('/login...') is in flight
  // rather than flashing null/blank before the navigation completes.
  if (!user) return <AppBootScreen />;

  // Signed in with an unverified email/password address — hold here until the
  // address is confirmed rather than bootstrapping an unverified account.
  if (needsEmailVerification) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-sm w-full">
          <VerifyEmailNotice email={user.email} />
        </div>
      </div>
    );
  }

  // Authenticated with Firebase, but the account bootstrap (POST
  // /api/me/bootstrap → GET /api/me) terminally failed — a network blip or an
  // API cold-start at sign-in that exhausted the auto-retries, or a genuine
  // auth/permission rejection. Surface a recovery card so the user can retry
  // instead of being dropped into a half-loaded shell (no org, no settings).
  if (bootstrapError && !me) {
    return <AccountLoadError onRetry={retryBootstrap} />;
  }

  // Hold while a `?w=` deep-link switches workspace (full navigation) so we
  // never evaluate the gate against the wrong tenant.
  if (me && workspaceHopPending) {
    return <AppBootScreen />;
  }

  // ENG-3641: wait for the post-bootstrap flags fetch before deciding gate vs
  // shell. On persistent settings failure, fall through with the gate off
  // (dark alpha — prefer a reachable shell over an infinite spinner).
  if (me && !featuresLoaded && !featuresFailed) {
    return <AppBootScreen />;
  }

  // Firm-website gate, dark behind `onboarding_gate`. When granted and
  // unanswered, block the shell until they submit. Skip while a staff
  // `?as=` hop is in the URL — AppShell's useStaffTaskDeepLink must mount
  // to impersonate (and apply `?w=`) without a full reload; the gate would
  // otherwise strand the hop (codex P2 on #2045).
  if (me && gateEnabled && !onboardingState && !staffDeepLinkPending()) {
    return <OnboardingGateScreen />;
  }

  return <>{children}</>;
}

/**
 * Boot screen shown while we resolve the Firebase session and bootstrap the
 * account (POST /api/me/bootstrap → GET /api/me).
 *
 * For a returning user this is a sub-second flash, so we show only the branded
 * mark + spinner. For a brand-new user the first bootstrap provisions an org +
 * memory store and can run behind a Cloud Run cold start — tens of seconds — so
 * after a short delay we reveal a "setting up" line so the wait reads as
 * progress rather than a hang. (Reported 2026-06-23: a new user saw a bare ~45s
 * spinner after verifying their email, with no indication anything was
 * happening.)
 *
 * Exported for the login page, which shows it while the initial auth state
 * (including a just-completed Google redirect round-trip) is still resolving
 * instead of flashing the sign-in form.
 */
export function AppBootScreen() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 2500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-5 px-6 text-center">
      <BrandMark
        alt="Deliverables AI"
        className="h-10 w-10 animate-pulse-dot"
      />
      <div className="w-6 h-6 border-2 border-border border-t-foreground rounded-full animate-spin" />
      {slow && (
        <p className="text-sm text-subtle max-w-xs leading-relaxed animate-fade-in">
          Setting up your workspace… this can take a moment the first time you
          sign in.
        </p>
      )}
    </div>
  );
}

function AccountLoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="max-w-md text-center space-y-4">
        <div className="text-3xl">🔌</div>
        <h2 className="text-lg font-semibold">Couldn’t load your account</h2>
        <p className="text-sm text-subtle">
          We couldn’t finish signing you in. This is usually a temporary
          network or server hiccup — please try again.
        </p>
        <button
          onClick={onRetry}
          className="text-xs px-3 py-1.5 rounded border border-border hover:bg-muted"
        >
          Retry
        </button>
      </div>
    </div>
  );
}
